import { useSyncExternalStore } from 'react';
import { useFilterBarContext } from '../state/FilterBarContext';
import type {
  FilterFieldPhase,
  FilterItemConfig,
} from '../types';
import type { SelectOptionType } from '../../Controls/Select/types';

export interface UseFilterFieldResult {
  value: unknown;
  options: SelectOptionType[];
  phase: FilterFieldPhase;
  error: string | null;
  isLoading: boolean;
  config: FilterItemConfig | undefined;
  setValue: (value: unknown) => void;
  clear: () => void;
}

const EMPTY_OPTIONS: SelectOptionType[] = [];

/**
 * Per-field accessor for use inside custom control renderers.
 *
 * Subscribes to THIS field's slice of the store — the reducer preserves
 * untouched fields' state identity, so an unrelated field's transition does
 * not re-render this component (per-field re-render granularity).
 */
export function useFilterField(name: string): UseFilterFieldResult {
  const ctx = useFilterBarContext();
  const { store } = ctx;
  const field = useSyncExternalStore(
    store.subscribe,
    () => store.getInternalState().fields[name],
    () => store.getInternalState().fields[name],
  );
  const config = ctx.itemsByName[name];

  return {
    value: field?.value,
    options: field?.options ?? EMPTY_OPTIONS,
    phase: field?.phase ?? 'IDLE',
    error: field?.error ?? null,
    isLoading: field
      ? field.phase === 'LOADING_OPTIONS' || field.phase === 'LOADING_DEFAULT'
      : false,
    config,
    setValue: (v) => store.set(name, v),
    clear: () => store.clear(name),
  };
}
